<script setup lang="ts">
import { getRuneName, runeCollection, runeSpecialFactor } from '@/core/rune'
import { useUserStore } from '@/stores/modules/user'
import type { RuneItemType, RuneSpecAttrType } from '@/types/rune'
import type { UserAttrType } from '@/types/user'
import { socketEmitAsync } from '@/utils/web-socket'
import RuneInfo from '@/views/item/components/RuneInfo.vue'
import { ref } from 'vue'
import { QuestionFilled } from '@element-plus/icons-vue'

const userStore = useUserStore()

const runeItemList = ref<RuneItemType[]>([])
function getAllRuneItem() {
  runeItemList.value = Object.entries(runeCollection).map(([id, item]) => {
    const basic: Partial<UserAttrType> = {}
    const special: RuneSpecAttrType[] = []
    const runeSpecAttrKeys = Object.keys(runeSpecialFactor[item.specialKey] || {})
    if (runeSpecAttrKeys.length) {
      runeSpecAttrKeys.forEach((key) => {
        const value = 20 / runeSpecAttrKeys.length
        special.push({
          key: item.specialKey as string,
          data: {
            [key]: value,
          },
        })
      })
    } else {
      if (item.soulType === 1) {
        basic.atk = 10
        basic.crtd = 10
      } else if (item.soulType === 2) {
        basic.heat = 10
        basic.hr = 10
      } else if (item.soulType === 3) {
        basic.atksp = 10
        basic.crt = 10
      }
    }
    return {
      id,
      runeId: Number(id),
      quality: 'legend',
      attrs: {
        basic,
        special,
      },
      isLock: false,
    }
  })
}
getAllRuneItem()

const runeInfo = ref<RuneItemType>()
function showDetail(item: RuneItemType) {
  runeInfo.value = item
}
const synthesisResShow = ref(false)
const synthesisRuneInfo = ref<RuneItemType>()
function handleSynthesis() {
  if (userStore.consumables.runeStar < 50) return ElMessage.warning('符石碎星不足')
  if (!runeInfo.value) return

  ElMessageBox.confirm(
    `<p class="affix">确定合成【<span class="legend">${getRuneName(
      runeInfo.value?.runeId,
    )}</span>】吗？（合成时，符石的属性分配仍是随机的，但评分不会低于 <span class="info">18</span>分）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      const res = await socketEmitAsync<RuneItemType>({ type: 'synthesisRune', data: { runeId: runeInfo.value?.runeId } })
      userStore.runeList.push(res)
      synthesisResShow.value = true
      synthesisRuneInfo.value = res
      userStore.consumables.runeStar -= 50
    })
    .catch(() => {})
}

const optionalSynthesisShow = ref(false)
const optionalRuneList = ref<RuneItemType[]>([])
const optionalRuneRadio = ref()
async function handleOpenOptionalSynthesis() {
  const res = await socketEmitAsync<RuneItemType[]>({ type: 'getOptionalRuneList' })
  optionalRuneList.value = res
  optionalSynthesisShow.value = true
}
function handleSynthesisOptionalSynthesis() {
  const runeInfo = optionalRuneList.value.find((item) => item.id === optionalRuneRadio.value)
  if (!runeInfo) return ElMessage.warning('未选中任何符石')
  ElMessageBox.confirm(`确认兑换${getRuneName(runeInfo.runeId)}符石吗？<b style="color: red">（操作不可撤回，请务必谨慎）</b>`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      const res = await socketEmitAsync<RuneItemType>({ type: 'sysnthesizeOptionalRune', data: { id: runeInfo.id } })
      userStore.runeList.push(res)
      synthesisResShow.value = true
      synthesisRuneInfo.value = res
      userStore.consumables.shineBrightlyStone--
    })
    .catch(() => {
      return
    })
}
</script>

<template>
  <div class="rune-collection">
    <div class="left border-wrap">
      <h4>符石列表</h4>

      <div class="item-btn-wrap btn-wrap">
        <CommonBtn
          v-for="item of runeItemList"
          :key="item.id"
          size="small"
          @done="showDetail(item)"
          :style="{ position: 'relative', borderColor: runeInfo?.id === item.id ? 'red' : '' }"
        >
          <span :class="item.quality"> {{ getRuneName(item.runeId) }}</span>
        </CommonBtn>
      </div>
    </div>
    <div class="right">
      <div class="action border-wrap">
        <h4>
          操作
          <div class="help-info">
            <el-popover placement="top" width="100%" trigger="click">
              <template #reference>
                <el-icon size="16"><QuestionFilled /></el-icon>
              </template>
              <div class="affix">
                <h4>符石图鉴说明</h4>
                <p>1. 符石列表中的符石属性代表最高评分时的<b>可能</b>出现的属性，仅作示例，与真实出率无关。</p>
                <p>
                  2. 通过收集<span class="awaken">符石碎星</span>，可进行指定符石合成，合成时，符石的最低评分不会小于 <span class="info">18</span>。
                </p>
              </div>
            </el-popover>
          </div>
        </h4>

        <p style="font-size: 12px; margin: 5px 0">持有符石碎星： {{ userStore.consumables.runeStar }}</p>
        <el-button type="primary" plain size="small" :disabled="!runeInfo" @click="handleSynthesis">合成（50 符石碎星）</el-button>
        <el-button
          type="success"
          plain
          size="small"
          v-show="userStore.consumables.shineBrightlyStone > 0"
          @click="handleOpenOptionalSynthesis"
          style="margin-top: 10px; margin-left: 0"
          >使用辉耀之石（{{ userStore.consumables.shineBrightlyStone }}）</el-button
        >
      </div>
      <div class="rune-info border-wrap">
        <h4>符石信息</h4>

        <template v-if="runeInfo">
          <RuneInfo :rune-info="runeInfo" :show-action="false" />
        </template>
      </div>
    </div>

    <el-dialog v-model="synthesisResShow" width="100%" style="max-width: 500px">
      <h2 class="affix">
        <span class="grow">合成成功</span>
      </h2>
      <template v-if="synthesisRuneInfo">
        <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
          <template #reference>
            <CommonBtn size="small">
              <span :class="synthesisRuneInfo.quality">{{ getRuneName(synthesisRuneInfo.runeId) }}</span>
            </CommonBtn>
          </template>
          <RuneInfo :rune-info="synthesisRuneInfo" :show-action="false" />
        </el-popover>
      </template>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="synthesisResShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>

    <el-dialog v-model="optionalSynthesisShow" width="100%" style="max-width: 500px">
      <h2 class="affix">
        <span class="grow">选择您需要兑换的符石</span>
      </h2>

      <el-radio-group v-model="optionalRuneRadio" class="radio-list">
        <el-radio v-for="item of optionalRuneList" :value="item.id">
          <el-popover placement="bottom" :width="300" trigger="click">
            <template #reference>
              <CommonBtn size="small" style="width: 100%">
                <span :class="item.quality">{{ getRuneName(item.runeId) }}</span>
              </CommonBtn>
            </template>
            <RuneInfo :rune-info="item" :show-action="false" />
          </el-popover>
        </el-radio>
      </el-radio-group>
      <template #footer>
        <p style="text-align: center">
          <el-button plain type="primary" size="small" @click="handleSynthesisOptionalSynthesis">确定</el-button>
          <el-button plain @click="optionalSynthesisShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.rune-collection {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-flow: row nowrap;
  flex: 1;
  justify-content: space-between;
  h4 {
    margin: 10px 0;
    display: flex;
    justify-content: center;
    position: relative;
    .help-info {
      position: absolute;
      margin-left: 10px;
      right: 30%;
    }
  }
  .border-wrap {
    padding: 0 5px;
  }
  .left {
    width: calc(50% - 5px);
    margin-bottom: 10px;
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-flow: column;
    .item-btn-wrap {
      overflow: auto;
      :deep() {
        .el-button:focus,
        .el-button:hover {
          background-color: unset;
        }
      }
    }
    .btn-wrap > div {
      margin-bottom: 10px;
    }
  }
  .right {
    width: calc(50% - 5px);
    display: flex;
    flex-flow: column nowrap;
    .action {
      margin-bottom: 10px;
      padding-bottom: 10px;
      .el-button {
        width: 100%;
      }
    }
    .rune-info {
      height: 100%;
    }
  }
  .radio-list {
    max-height: 50vh;
    overflow: auto;
    display: flex;
    flex-flow: column nowrap;
    .el-radio {
      width: 80%;
      margin-bottom: 10px;
      margin-right: 0;
      :deep(.el-radio__label) {
        width: 100% !important;
      }
    }
  }
}
</style>
